import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, asyncScheduler, of, scheduled } from 'rxjs';
import { map } from 'rxjs/operators';

import { ProcedureInstanceVisibilityService } from '../../../common-shared-module/services/procedure-instance/procedure-instance-visibility.service';
import { EFormHeaderAndOneSectionModelValue, EFormHeaderModelValue, EFormSectionModelValue, EFormSectionModelValueResponse } from '../../model/eform/eform-api.model';


@Injectable({
    providedIn: 'root'
})
export class EFormValueService {

    protected eFormSectionModelValueResponseCache: Map<string, EFormSectionModelValueResponse> = new Map()
    protected eFormHeaderModelValueCache: Map<string, { ok: boolean, entity: EFormHeaderModelValue, messageCode: string }> = new Map()

    protected resourceUrl: string = "/rest/publicarea/form"

    constructor(private http: HttpClient, protected procedureInstanceVisibilityService: ProcedureInstanceVisibilityService) { }

    public getEFormSectionModelValueCached(serviceInstanceId: number, procedureInstanceId: number, sectionId: number): EFormSectionModelValueResponse | undefined {
        return this.eFormSectionModelValueResponseCache.get(serviceInstanceId + "-" + procedureInstanceId + "-" + sectionId)
    }

    public getEFormSectionModelValue(serviceInstanceId: number, procedureInstanceId: number, sectionId: number): Observable<EFormSectionModelValueResponse> {
        let data = this.eFormSectionModelValueResponseCache.get(serviceInstanceId + "-" + procedureInstanceId + "-" + sectionId)

        if (data != null) {
            //We can't deliver the same cached object because the changes in the view are made on the same object without been stored in the server side. 
            //This way if the server side fails in the put operation the changes are not cached
            let dataString = JSON.stringify(data)
            return of(JSON.parse(dataString))
        }

        let headers = { headers: this.procedureInstanceVisibilityService.createHeaderRef(procedureInstanceId) }

        return this.http.get<EFormSectionModelValueResponse>(`${this.resourceUrl}/getEFormSectionModelValue/${serviceInstanceId}/${procedureInstanceId}/${sectionId}`, headers).pipe(map(result => {
            if (result.ok == false) {
                return result
            }

            this.convertEFormSectionModelValueResponseToJsonObject(result, serviceInstanceId, procedureInstanceId, sectionId);

            return result
        }))
    }


    private convertEFormSectionModelValueResponseToJsonObject(result: EFormSectionModelValueResponse, serviceInstanceId: number, procedureInstanceId: number, sectionId: number) {
        let value: any = null;
        if (result.entity.value == '') {
            value = null;
        } else {
            value = JSON.parse(result.entity.value);
            value = JSON.parse(value.value);
        }

        let data: EFormSectionModelValue = { sectionId: result.entity.sectionId, value: value };
        result.entity = data;
        this.eFormSectionModelValueResponseCache.set(serviceInstanceId + "-" + procedureInstanceId + "-" + sectionId, result);
    }

    public putEFormSectionModelValue(serviceInstanceId: number, procedureInstanceId: number, value: EFormSectionModelValue): Observable<{ ok: boolean, entity: Boolean, messageCode: string }> {
        let beforeChange = value.value
        value.value = JSON.stringify(value.value)

        let headers = { headers: this.procedureInstanceVisibilityService.createHeaderRef(procedureInstanceId) }

        return this.http.post<{ ok: boolean, entity: Boolean, messageCode: string }>(`${this.resourceUrl}/putEFormSectionModelValue/${serviceInstanceId}/${procedureInstanceId}`, value, headers).pipe(map(result => {
            if (result.ok == false) {
                return result
            }
            let data = this.eFormSectionModelValueResponseCache.get(serviceInstanceId + "-" + procedureInstanceId + "-" + value.sectionId)

            if (data != null) {
                //Update the value with the modified one!:
                data.entity.value = beforeChange

            }
            return result
        }))
    }


    public putEFormHeaderAndOneSectionModelValue(serviceInstanceId: number, procedureInstanceId: number, value: EFormHeaderAndOneSectionModelValue): Observable<{ ok: boolean, messageCode: string }> {
        let beforeChange = value.section.value
        value.section.value = JSON.stringify(value.section.value)

        let headers = { headers: this.procedureInstanceVisibilityService.createHeaderRef(procedureInstanceId) }

        return this.http.post<{ ok: boolean, messageCode: string, entity: EFormSectionModelValue[] }>(`${this.resourceUrl}/putEFormHeaderAndOneSectionModelValue/${serviceInstanceId}/${procedureInstanceId}`, value, headers).pipe(map(result => {
            if (!result.ok) {
                return { ok: result.ok, messageCode: result.messageCode }
            }

            let dataSection = this.eFormSectionModelValueResponseCache.get(serviceInstanceId + "-" + procedureInstanceId + "-" + value.section.sectionId)
            if (dataSection != null) {
                //Update the value with the modified one!:
                dataSection.entity.value = beforeChange
            }

            let dataHeader = this.eFormHeaderModelValueCache.get(`${serviceInstanceId}-${procedureInstanceId}`);
            if (dataHeader != null) {
                dataHeader.entity = value.header;
            }

            if (result.entity && result.entity.length > 0) {
                for (let section of result.entity) {
                    let sectionResponse = { ok: true, entity: section, messageCode: "" }
                    this.convertEFormSectionModelValueResponseToJsonObject(sectionResponse, serviceInstanceId, procedureInstanceId, section.sectionId);
                }
            }

            return { ok: result.ok, messageCode: result.messageCode }
        }))
    }


    public getEFormHeaderModelValue(serviceInstanceId: number, procedureInstanceId: number): Observable<{ ok: boolean, entity: EFormHeaderModelValue, messageCode: string }> {

        let data = this.eFormHeaderModelValueCache.get(`${serviceInstanceId}-${procedureInstanceId}`)
        if (data != null) {
            return scheduled(of(data), asyncScheduler)
        }

        let headers = { headers: this.procedureInstanceVisibilityService.createHeaderRef(procedureInstanceId) }

        return this.http.get<{ ok: boolean, entity: EFormHeaderModelValue, messageCode: string }>(`${this.resourceUrl}/getEFormHeaderModelValue/${serviceInstanceId}/${procedureInstanceId}`, headers).pipe(map(p => {
            if (p.ok == false) {
                return p
            }

            this.eFormHeaderModelValueCache.set(`${serviceInstanceId}-${procedureInstanceId}`, p)
            return p
        }))
    }


    public putEFormHeaderModelValue(value: EFormHeaderModelValue): Observable<{ ok: boolean, entity: Boolean, messageCode: string }> {

        let headers = { headers: this.procedureInstanceVisibilityService.createHeaderRef(value.procedureInstanceId) }


        return this.http.post<{ ok: boolean, entity: Boolean, messageCode: string }>(`${this.resourceUrl}/putEFormHeaderModelValue`, value, headers).pipe(map(p => {

            let data = this.eFormHeaderModelValueCache.get(`${value.serviceInstanceId}-${value.procedureInstanceId}`)
            if (data != null) {
                data.entity = value
            }

            return p
        }))
    }

    preloadEFormHeaderModelValueCache(serviceInstanceId: number, procedureInstanceId: number, value: { ok: boolean, entity: EFormHeaderModelValue, messageCode: string }) {
        this.eFormHeaderModelValueCache.set(`${serviceInstanceId}-${procedureInstanceId}`, value)
    }

    preloadFormSectionModelValueResponseCache(serviceInstanceId: number, procedureInstanceId: number, sectionId: number, value: EFormSectionModelValueResponse) {
        this.convertEFormSectionModelValueResponseToJsonObject(value, serviceInstanceId, procedureInstanceId, sectionId)
    }

}
